import React, {
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { matchSorter } from "match-sorter";
import { Row, SortingRule } from "react-table";
import { Button, Spinner } from "@src/components/elements";
import {
  StyledDivTable,
  StyledTableRoot,
} from "@src/components/modules/Table/StyledTable";
import { SelectOption } from "@src/components/elements/Select/SelectOption";
import { useScrollbarWidth } from "react-use";
import {
  CountProps,
  HeaderSlotProps,
  TableFilterProps,
  TableProperties,
} from "@src/components/modules/Table/tableInterfaces";
import { PaginatedTableBody } from "@src/components/modules/Table/PaginatedTableBody";
import { TablePagination } from "@src/components/modules/Table/TablePagination";
import { TableHead } from "@src/components/modules/Table/TableHead";
import { TableHeader } from "@src/components/modules/Table/TableHeader";
import TableCsvDownload from "@src/components/modules/Table/TableCsvDownload";
import { DownloadIcon } from "@src/components/elements/assets/DownloadIcon";
import { ExportCsvDataTrigger } from "@src/components/modules/Table/TableActions/ExportCsvData";
import { useTableConfiguration } from "@src/components/modules/Table/useTableConfiguration";
import { useFilterQuery } from "@src/hooks/useFilterQuery";
import { z } from "zod";

const defaultPropGetter = () => ({});

function fuzzyTextFilterFn(rows: Row[], id: string, filterValue: string) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val: string) => !val;

export const tableFilterTypes = {
  fuzzyText: fuzzyTextFilterFn,
  text: (rows: Row[], id: string, filterValue: string) => {
    return rows.filter((row) => {
      const rowValue = row.values[id];
      return rowValue !== undefined
        ? String(rowValue)
            .toLowerCase()
            .startsWith(String(filterValue).toLowerCase())
        : true;
    });
  },
  multiValueByName: (rows: Row[], id: string, filterValue: SelectOption[]) => {
    return rows.filter((row) => {
      const multiValue = z
        .array(z.object({ name: z.string() }))
        .parse(row.values[id]);
      const matches = multiValue.map((val) => {
        return val.name
          .toLowerCase()
          .includes(String(filterValue).toLowerCase());
      });
      return matches.includes(true);
    });
  },
  textOR: (rows: Row[], id: string, filterValue: SelectOption[]) => {
    return rows.filter((row) => {
      const rowValue = row.values[id];
      if (rowValue === undefined) {
        return true;
      }
      for (const value of filterValue) {
        if (
          String(rowValue)
            .toLowerCase()
            .startsWith(String(value.value).toLowerCase())
        ) {
          return true;
        }
      }
      return false;
    });
  },
};

export type TableProps<T extends Record<string, unknown>> = PropsWithChildren<
  TableProperties<T>
> &
  CountProps &
  HeaderSlotProps & {
    footerSlot?: ReactNode;
  } & TableFilterProps<T> & {
    onSortByChange?: (sorted: SortingRule<T>[]) => void;
  };

const TableComponent = <T extends Record<string, unknown>>(
  props: TableProps<T>,
): ReactElement<TableProps<T>> => {
  const {
    toggleFilters: toggleFiltersFromQuery,
    hasFilters: hasFiltersFromQuery,
  } = useFilterQuery();
  const {
    columns,
    data,
    showCount = false,
    countLabel,
    title,
    renderRowSubComponent,
    backgroundColor,
    getColumnProps = defaultPropGetter,
    getCellProps = defaultPropGetter,
    headerSlot,
    canFilter,
    canExport = false,
    hasStickyHeader = true,
    stickyHeaderOffsetTop = 0,
    padding,
    headerSlotPosition,
    exportPosition = "right",
    borderCells = false,
    getHeaderProps,
    getInstanceCallback,
    autoResetSortBy = false,
    getRowProps,
    transformExportedColumn,
    isExternallyControlled = false,
    onClickNextPage,
    onClickPrevPage,
    onClickPageNum,
    onClickShowAllPages,
    page: initialPage = 1,
    isVirtualized = false,
    virtualTableHeight = undefined,
    pageSize = 20,
    loading,
    footerSlot,
    fixedWidth = false,
    className,
    headerBackground,
    customCsvExport,
    filters,
    setFilters,
    toggleFilters = toggleFiltersFromQuery,
    hasFilters = hasFiltersFromQuery,
    csvDownloadRef,
    virtualizedRecordsInfoSlot,
    filtersFromOutside,
    setFilterOutside,
    headerButtonSize,
  } = props;

  const scrollbarWidth = useScrollbarWidth();

  const internalCsvDownloadRef = useRef<ExportCsvDataTrigger>(null);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setAllFilters,
    totalColumnsWidth,
    state: { pageIndex },
    showTableHeader,
  } = useTableConfiguration({
    columns,
    data,
    initialPage,
    autoResetSortBy,
    filters,
    canExport,
    canFilter,
    filterTypes: tableFilterTypes,
    fixedWidth,
    getInstanceCallback,
    headerSlot,
    isExternallyControlled,
    pageSize,
    setFilters,
    title,
    setFilterOutside,
    filtersFromOutside,
  });

  useEffect(() => {
    setPageSize(pageSize);
  }, [setPageSize, pageSize]);

  useEffect(() => {
    if (canFilter) {
      setAllFilters([]);
    }
  }, [canFilter, setAllFilters]);

  const tableWrapRef = useRef<HTMLDivElement>(null);

  const handleClickPreviousPage = useCallback(() => {
    previousPage();
    onClickPrevPage && onClickPrevPage();
  }, [onClickPrevPage, previousPage]);

  const handleClickNextPage = useCallback(() => {
    nextPage();
    onClickNextPage && onClickNextPage();
  }, [onClickNextPage, nextPage]);

  const handleClickPageNumber = useCallback(
    (pageIndex: number) => {
      gotoPage(pageIndex);
      onClickPageNum && onClickPageNum(pageIndex);
    },
    [onClickPageNum, gotoPage],
  );

  const handleClickShowAllPages = useCallback(() => {
    setPageSize(data.length);
    onClickShowAllPages && onClickShowAllPages();
  }, [setPageSize, data.length, onClickShowAllPages]);

  return (
    <StyledTableRoot
      padding={padding}
      backgroundColor={backgroundColor}
      hasStickyHeader={hasStickyHeader}
      headerSlotPosition={headerSlotPosition}
      className={className}
      theadWidth={
        isVirtualized && virtualTableHeight
          ? totalColumnsWidth + (scrollbarWidth || 0)
          : undefined
      }
    >
      {showTableHeader ? (
        <TableHeader
          csvDownloadRef={csvDownloadRef ?? internalCsvDownloadRef}
          canExport={canExport}
          canFilter={canFilter || false}
          toggleFilters={toggleFilters}
          title={title || ""}
          headerSlot={headerSlot}
          loading={loading}
          buttonSize={headerButtonSize}
          exportPosition={exportPosition}
        />
      ) : null}

      {isVirtualized && (
        <div className="row pad small">
          <div>{rows.length} records</div>
          {virtualizedRecordsInfoSlot}
          {canExport && !isExternallyControlled ? (
            <Button
              variant="tertiary"
              onClick={() => {
                const coalescedRef = csvDownloadRef ?? internalCsvDownloadRef;
                if (coalescedRef && "current" in coalescedRef)
                  coalescedRef.current?.download();
              }}
            >
              Export
              <DownloadIcon />
            </Button>
          ) : null}
          {loading && <Spinner />}
        </div>
      )}
      <div
        ref={tableWrapRef}
        style={{
          height:
            isVirtualized && virtualTableHeight
              ? virtualTableHeight
              : undefined,
          overflow: isVirtualized ? "auto" : undefined,
          width: isVirtualized ? "100%" : undefined,
        }}
        className="table-wrap"
      >
        <StyledDivTable
          borderCells={borderCells}
          {...getTableProps()}
          style={{ ...getTableProps().style, minWidth: "fit-content" }}
          hasStickyHeader={hasStickyHeader}
          stickyHeaderOffsetTop={stickyHeaderOffsetTop}
          headerBackground={headerBackground}
        >
          <TableHead
            getHeaderProps={getHeaderProps}
            headerGroups={headerGroups}
            headerBackground={headerBackground}
            hasFilters={hasFilters}
          />
          <PaginatedTableBody
            getTableBodyProps={getTableBodyProps}
            page={page}
            prepareRow={prepareRow}
            getRowProps={getRowProps}
            getColumnProps={getColumnProps}
            getCellProps={getCellProps}
            renderRowSubComponent={renderRowSubComponent}
            tableWrapRef={tableWrapRef}
          />
        </StyledDivTable>
      </div>
      <div className="row">
        {!isVirtualized && (
          <TablePagination
            canNextPage={canNextPage}
            canPreviousPage={canPreviousPage}
            countLabel={countLabel || "Records"}
            dataLength={data.length}
            onClickNextPage={handleClickNextPage}
            onClickPrevPage={handleClickPreviousPage}
            pageCount={pageCount}
            pageIndex={pageIndex}
            pageOptions={pageOptions}
            pageSize={pageSize}
            onClickPageNum={handleClickPageNumber}
            onClickShowAllPages={handleClickShowAllPages}
            pageLength={page.length}
            showCount={showCount}
          />
        )}
        {footerSlot}
      </div>
      <TableCsvDownload
        rows={rows}
        customCsvExport={customCsvExport}
        transformExportedColumn={transformExportedColumn}
        ref={csvDownloadRef ?? internalCsvDownloadRef}
      />
    </StyledTableRoot>
  );
};

const typedMemo: <T>(c: T) => T = React.memo;
export const Table = typedMemo(TableComponent);
